import { AccessDeniedPage } from "@dashboard/components/AccessDeniedPage";
import { HasHseAccess } from "@dashboard/components/HasHseAccess";
import { ListModalRouter } from "@dashboard/components/common/ListModalRouter";
import { TaskModalRouter } from "@dashboard/components/common/TaskModalRouter";
import HseAppProviders from "@dashboard/HseAppProviders";
import { dashboardWidgetsOptions } from "@dashboard/api/queryApi";
import type { DashboardWidget } from "@dashboard/api/productAPI";
import { useDashboardProfile } from "@dashboard/hooks/useDashboardProfile";
import { useHseInstance } from "@dashboard/hooks/useHseInstance";
import useUser from "@dashboard/hooks/useUser";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, Outlet, useParams } from "@tanstack/react-router";
import { PageLayout, PageHeaderCenter, cn } from "frontend-components";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
    useSelectedCustomer,
    useSelectedCustomerIdParam,
} from "@/features/hub/selectedCustomerContext";

export const dashboardModalSearchSchema = z.object({
    task: z
        .union([z.literal("new"), z.coerce.number().int().positive()])
        .optional()
        .catch(undefined),
    edit: z.coerce.number().pipe(z.literal(1)).optional().catch(undefined),
    list: z
        .union([z.literal("new"), z.coerce.number().int().positive()])
        .optional()
        .catch(undefined),
    editList: z.coerce.number().pipe(z.literal(1)).optional().catch(undefined),
    listFrom: z.coerce.number().int().positive().optional().catch(undefined),
    listTemplate: z.coerce.number().pipe(z.literal(1)).optional().catch(undefined),
});

export type DashboardModalSearch = z.infer<typeof dashboardModalSearchSchema>;

export const Route = createFileRoute("/_hub/$customerId/dashboard")({
    component: RouteComponent,
    validateSearch: dashboardModalSearchSchema,
    staticData: {
        titleFn: null,
    },
});

function RouteComponent() {
    const { selectedCustomerId, dashboardNavigation, isDashboardNavigationPending } =
        useSelectedCustomer();

    return (
        <HseAppProviders
            selectedCustomerId={selectedCustomerId}
            dashboardNavigation={dashboardNavigation}
            isDashboardNavigationPending={isDashboardNavigationPending}
        >
            <HseLayoutContent />
        </HseAppProviders>
    );
}

const tabBaseClass = cn(
    "inline-flex items-center rounded-[3px] px-3.5 py-1.5 text-sm font-medium transition-colors",
);
const tabActiveProps = {
    className: cn("bg-compendia-card text-compendia-foreground shadow-sm"),
};
const tabInactiveProps = {
    className: cn("text-compendia-gray-60 hover:text-compendia-foreground"),
};

function HseLayoutContent() {
    const user = useUser();
    const { t } = useTranslation();
    const customerId = useSelectedCustomerIdParam();

    return (
        <HasHseAccess fallback={<AccessDeniedPage />}>
            <PageLayout
                pageTitle={
                    <h1 className="text-h2 text-compendia-foreground min-w-0 truncate font-medium md:text-2xl">
                        {t("dashboard.goodMorningMessage", {
                            name: user?.firstName,
                        })}
                    </h1>
                }
            >
                <PageHeaderCenter>
                    <DashboardTabs customerId={customerId} />
                </PageHeaderCenter>
                <Outlet />
                <TaskModalRouter />
                <ListModalRouter />
            </PageLayout>
        </HasHseAccess>
    );
}

function DashboardTabs({ customerId }: { customerId: string }) {
    const dashboardProfileParams = useParams({
        from: "/_hub/$customerId/dashboard/$profileId",
        shouldThrow: false,
    });
    const { t } = useTranslation();
    const { productInstanceId } = useHseInstance();
    const dashboardProfile = useDashboardProfile(
        productInstanceId,
        t("dashboard.myDashboard"),
        dashboardProfileParams ? Number(dashboardProfileParams.profileId) : undefined,
    );
    const { data: widgetProvisioning = [] } = useQuery(dashboardWidgetsOptions(productInstanceId));

    const hasVisibleWidget = (widgetId: DashboardWidget) =>
        dashboardProfile.profile?.config.activeWidgets.includes(widgetId) === true &&
        widgetProvisioning.some(
            (provisioning) => provisioning.widget === widgetId && provisioning.enabled,
        );
    const showAnnualCycle = hasVisibleWidget("YEARLY_CYCLE");
    const showChecklists = hasVisibleWidget("LISTS");
    const selectedProfileId = dashboardProfile.profile?.id;

    if (!showAnnualCycle && !showChecklists) {
        return null;
    }

    return (
        <ul className="bg-compendia-greige-40 inline-flex items-center gap-1 rounded-[5px] p-1">
            <li>
                {selectedProfileId ? (
                    <Link
                        to="/$customerId/dashboard/$profileId"
                        params={{
                            customerId,
                            profileId: String(selectedProfileId),
                        }}
                        className={tabBaseClass}
                        activeProps={tabActiveProps}
                        inactiveProps={tabInactiveProps}
                        activeOptions={{ exact: true }}
                    >
                        {t("page.dashboard")}
                    </Link>
                ) : (
                    <Link
                        to="/$customerId/dashboard"
                        params={{ customerId }}
                        className={tabBaseClass}
                        activeProps={tabActiveProps}
                        inactiveProps={tabInactiveProps}
                        activeOptions={{ exact: true }}
                    >
                        {t("page.dashboard")}
                    </Link>
                )}
            </li>
            {showAnnualCycle && selectedProfileId ? (
                <li>
                    <Link
                        to="/$customerId/dashboard/$profileId/annual-cycle"
                        params={{
                            customerId,
                            profileId: String(selectedProfileId),
                        }}
                        className={tabBaseClass}
                        activeProps={tabActiveProps}
                        inactiveProps={tabInactiveProps}
                    >
                        {t("page.annualCycle")}
                    </Link>
                </li>
            ) : null}
            {showChecklists && selectedProfileId ? (
                <li>
                    <Link
                        to="/$customerId/dashboard/$profileId/lists"
                        params={{
                            customerId,
                            profileId: String(selectedProfileId),
                        }}
                        className={tabBaseClass}
                        activeProps={tabActiveProps}
                        inactiveProps={tabInactiveProps}
                    >
                        {t("page.checklists")}
                    </Link>
                </li>
            ) : null}
        </ul>
    );
}
